(() => {
  if (customElements.get('cl-native-product-options')) return;
  let sequence = 0;
  const displayLabel = (locale, labels, value) => locale === 'en' && labels && Object.hasOwn(labels, value) && typeof labels[value] === 'string' ? labels[value] : value;
  const selectionValue = (field, values) => field.values.filter(item => values.includes(item.value)).map(item => item.value).join(', ');
  const withinSelectionLimit = (field, values) => Array.isArray(values) && new Set(values).size === values.length && values.length <= field.maxSelections;
  const nebbiolinaPrimary = (variants, id) => {
    const mapping = { Bianco: 'Bianca', Rosso: 'Rossa', Rosa: 'Rosa', Viola: 'Viola' };
    const variant = variants.find(entry => String(entry.id) === String(id));
    return variant && Object.hasOwn(mapping, variant.colour) ? { value: mapping[variant.colour], available: variant.available === true } : null;
  };
  const validate = (field, value) => {
    if (field.type === 'color-swatches') {
      if (!Array.isArray(value) || new Set(value).size !== value.length) return false;
      return value.every(v => field.values.some(item => item.value === v)) && value.length >= field.minSelections && value.length <= field.maxSelections;
    }
    if (typeof value !== 'string') return false;
    if (!value.trim()) return !field.required;
    return value.length >= field.minLength && (field.maxLength === null || value.length <= field.maxLength);
  };
  window.CLNativeProductOptionsContract = Object.freeze({ selectionValue, validate, withinSelectionLimit, nebbiolinaPrimary, displayLabel });
  customElements.define('cl-native-product-options', class extends HTMLElement {
    connectedCallback() {
      if (this.ready || this.initializing) return;
      this.initializing = true;
      this.form = this.closest('form');
      this.message = this.querySelector('[data-cl-native-block-message]');
      this.ready = false;
      try {
        if (!this.form || this.form.getAttribute('id') !== this.dataset.clNativeFormId) throw new Error('FORM_OWNER');
        this.definition = JSON.parse(this.querySelector('[data-cl-native-definition]').textContent);
        this.locale = this.dataset.clNativeLocale?.toLowerCase().split('-')[0] === 'en' ? 'en' : 'it';
        this.displayLabels = JSON.parse(this.querySelector('[data-cl-native-display-labels]')?.textContent || '{}');
        if (!this.displayLabels || Array.isArray(this.displayLabels) || typeof this.displayLabels !== 'object') throw new Error('DISPLAY_LABELS');
        if (!this.definition || this.definition.productId !== this.dataset.productId || !this.definition.fields?.length) throw new Error('DEFINITION');
        this.instance = `cl-native-${++sequence}`;
        this.fields = [];
        this.surface = document.createElement('div');
        this.surface.className = 'cl-native-options';
        this.definition.fields.forEach((field, index) => this.renderField(field, index));
        this.append(this.surface);
        this.letteringRoot = this.closest('product-info');
        this.letteringUrlBlocked = false;
        const letteringContext = this.letteringChoiceContext();
        this.letteringUiExpected = Boolean(letteringContext);
        this.letteringErrorLabel = letteringContext?.ui.dataset.clLetteringError;
        if (letteringContext) {
          const urlVariant = new URL(window.location.href).searchParams.get('variant');
          const initialVariant = [...this.form.elements].find(control => control.name === 'id');
          if (urlVariant !== null && urlVariant !== initialVariant?.value) {
            this.letteringUrlBlocked = true;
            letteringContext.ui.querySelectorAll('[data-cl-lettering-choice]').forEach(control => { control.checked = false; });
            const note = letteringContext.ui.querySelector('[data-cl-lettering-legacy-note]');
            if (note) note.hidden = false;
          }
          this.letteringChoiceListener = event => {
            if (!event.target.matches('[data-cl-lettering-choice]')) return;
            const context = this.letteringChoiceContext();
            if (!context?.ui.contains(event.target) || event.target.disabled) return;
            this.letteringUrlBlocked = false;
          };
          this.letteringRoot.addEventListener('change', this.letteringChoiceListener, true);
        }
        this.changeListener = event => {
          if (event.target.name === 'id') {
            try {
              this.syncRoseFields();
              this.syncNebbiolinaFields();
            } catch {
              this.fail();
            }
          }
          if (this.contains(event.target)) {
            const item = this.fields.find(entry => entry.choices?.contains(event.target));
            if (item) {
              const values = [...item.choices.querySelectorAll('input:checked')].map(choice => choice.value);
              if (!withinSelectionLimit(item.field, values) && event.target.checked) {
                event.target.checked = false;
                item.feedback.textContent = this.dataset.limitReachedLabel.replace('__MAX__', String(item.field.maxSelections));
              } else item.feedback.textContent = '';
            }
            this.syncProperties();
            this.syncSelectionFeedback();
          }
        };
        this.form.addEventListener('change', this.changeListener);
        this.submitListener = event => {
          if (!this.prepareSubmission()) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        };
        this.form.addEventListener('submit', this.submitListener, true);
        this.syncRoseFields();
        this.syncNebbiolinaFields();
        this.syncSelectionFeedback();
        if (this.hasCollision()) throw new Error('DUPLICATE_OWNER');
        this.ready = true;
        this.message.hidden = true;
        const button = this.form.querySelector('[name="add"]');
        if (button && (this.dataset.currentAvailable ?? button.dataset.clNativeInitialAvailable) === 'true' && this.letteringChoiceReady()) button.disabled = false;
        this.form.querySelector('cl-rose-product-mix')?.syncPurchaseControls?.();
      } catch (error) {
        this.dataset.initializationError = error.message;
        this.message.hidden = false;
        this.fields?.forEach(item => { item.input.disabled = true; });
      }
      this.initializing = false;
    }
    disconnectedCallback() {
      this.form?.removeEventListener('change', this.changeListener);
      this.form?.removeEventListener('submit', this.submitListener, true);
      this.letteringRoot?.removeEventListener('change', this.letteringChoiceListener, true);
      this.surface?.remove();
      this.ready = false;
    }
    display(value) {
      return displayLabel(this.locale, this.displayLabels, value);
    }
    renderField(field, index) {
      if (!['text-box', 'text-area', 'color-swatches'].includes(field.type)) throw new Error('FIELD_TYPE');
      const wrapper = document.createElement('fieldset');
      wrapper.className = 'cl-native-option';
      wrapper.dataset.columnWidth = String(field.width);
      const id = `${this.instance}-${index}`;
      const title = document.createElement('legend');
      title.id = `${id}-label`;
      title.className = 'cl-native-option__label';
      title.textContent = this.display(field.label) + (field.required ? ' *' : '');
      wrapper.append(title);
      const input = document.createElement(field.type === 'text-area' ? 'textarea' : 'input');
      if (field.type !== 'text-area') input.type = field.type === 'color-swatches' ? 'hidden' : 'text';
      input.name = `properties[${field.key}]`;
      input.dataset.clNativeOwned = 'true';
      input.id = id;
      input.setAttribute('aria-labelledby', title.id);
      input.value = '';
      const item = { field, sourceValues: field.values, wrapper, input, id, skipped: false };
      if (field.type === 'color-swatches') {
        item.choices = document.createElement('div');
        item.choices.className = 'cl-native-option__swatches';
        wrapper.append(item.choices);
        this.renderChoices(item, field.values);
        const limits = document.createElement('small');
        limits.className = 'cl-native-option__help';
        limits.textContent = this.dataset.limitsLabel.replace('__MIN__', String(field.minSelections)).replace('__MAX__', String(field.maxSelections));
        wrapper.append(limits);
        item.counter = document.createElement('small');
        item.counter.className = 'cl-native-option__help';
        item.feedback = document.createElement('small');
        item.feedback.className = 'cl-native-option__help';
        item.feedback.setAttribute('role', 'status');
        item.feedback.setAttribute('aria-live', 'polite');
        wrapper.append(item.counter, item.feedback);
        if (!field.required) {
          const clear = document.createElement('button');
          clear.type = 'button';
          clear.className = 'button button--tertiary';
          clear.textContent = this.dataset.clearLabel;
          clear.addEventListener('click', () => {
            item.choices.querySelectorAll('input').forEach(choice => { choice.checked = false; });
            this.syncProperties();
            item.feedback.textContent = '';
            this.syncSelectionFeedback();
          });
          wrapper.append(clear);
        }
      } else {
        input.className = 'cl-native-option__text';
        input.placeholder = this.display(field.placeholder);
        input.required = field.required;
        if (field.htmlMaxLength?.recorded === true && Number.isSafeInteger(field.htmlMaxLength.value) && field.htmlMaxLength.value >= 0) input.maxLength = field.htmlMaxLength.value;
        if (field.minLength) input.minLength = field.minLength;
        if (field.normalizeCircumference) input.inputMode = 'decimal';
      }
      wrapper.append(input);
      if (field.help) {
        const help = document.createElement('small');
        help.id = `${id}-help`;
        help.className = 'cl-native-option__help';
        help.textContent = this.display(field.help);
        wrapper.append(help);
        input.setAttribute('aria-describedby', help.id);
      }
      if (field.information) {
        const information = document.createElement('p');
        information.className = 'cl-native-option__information';
        information.textContent = this.display(field.information);
        wrapper.append(information);
      }
      this.surface.append(wrapper);
      this.fields.push(item);
    }
    renderChoices(item, values) {
      const previous = item.choices ? [...item.choices.querySelectorAll('input:checked')].map(el => el.value) : [];
      item.choices.replaceChildren();
      for (const [index, option] of values.entries()) {
        const label = document.createElement('label');
        label.className = 'cl-native-option__named-choice';
        const choice = document.createElement('input');
        choice.type = item.field.multiple ? 'checkbox' : 'radio';
        choice.name = `${item.id}-choice`;
        choice.id = `${item.id}-${index}`;
        choice.value = option.value;
        choice.checked = previous.includes(option.value);
        label.htmlFor = choice.id;
        const text = document.createElement('span');
        text.textContent = this.display(option.value);
        if (Array.isArray(option.rgb) && option.rgb.length === 3 && option.rgb.every(n => Number.isInteger(n) && n >= 0 && n <= 255)) {
          const chip = document.createElement('span');
          chip.className = 'cl-native-option__chip';
          chip.style.backgroundColor = `rgb(${option.rgb.join(',')})`;
          chip.setAttribute('aria-hidden', 'true');
          label.append(choice, chip, text);
        } else label.append(choice, text);
        item.choices.append(label);
      }
    }
    syncSelectionFeedback() {
      for (const item of this.fields) {
        if (!item.choices || !item.counter) continue;
        const count = item.choices.querySelectorAll('input:checked').length;
        item.counter.textContent = this.dataset.selectionCountLabel.replace('__COUNT__', String(count)).replace('__MAX__', String(item.field.maxSelections));
      }
    }
    syncRoseFields() {
      const roseFields = this.fields.filter(item => ['rose-crown', 'rose-bow'].includes(item.field.behavior));
      if (!roseFields.length) return;
      const mix = this.form.querySelector('cl-rose-product-mix');
      if (!mix || typeof mix.sync !== 'function') throw new Error('ROSE_COMPONENT_NOT_READY');
      mix.sync();
      for (const item of roseFields) {
        item.skipped = Boolean(mix.active);
        item.wrapper.hidden = item.skipped || item.field.behavior === 'rose-crown';
        item.input.disabled = item.skipped;
        if (item.field.behavior === 'rose-crown') {
          item.input.value = item.skipped ? '' : (mix.frame?.colour || '');
          item.input.dataset.clNativeRoseUniform = 'true';
        } else if (!item.skipped) {
          const colours = window.CLRoseProductMixContract.coloursFor(mix.catalog.colours, mix.frame, 'bow');
          // Preserve the owner's original Easify choices literally. They are
          // customisation properties, not new Shopify inventory variants.
          const values = mix.frame ? [...item.sourceValues] : [];
          for (const colour of colours) {
            if (!values.some(option => option.value === colour.label)) values.push({ value: colour.label, rgb: null });
          }
          const signature = JSON.stringify(values);
          if (signature !== item.signature) {
            item.signature = signature;
            item.field = { ...item.field, values, multiple: false, minSelections: 1, maxSelections: 1 };
            this.renderChoices(item, values);
          }
        }
        item.choices.querySelectorAll('input').forEach(el => { el.disabled = item.skipped || item.field.behavior === 'rose-crown'; });
      }
      this.syncProperties();
      this.syncSelectionFeedback();
    }
    syncNebbiolinaFields() {
      const item = this.fields.find(entry => entry.field.behavior === 'nebbiolina-variant');
      if (!item) return;
      const variants = JSON.parse(this.querySelector('[data-cl-nebbiolina-variants]')?.textContent || 'null');
      if (!Array.isArray(variants) || variants.length !== 4 || new Set(variants.map(variant => variant.colour)).size !== 4) throw new Error('NEBBIOLINA_VARIANTS');
      const primary = nebbiolinaPrimary(variants, this.form.querySelector('[name="id"]')?.value);
      if (!primary || !item.field.values.some(value => value.value === primary.value)) throw new Error('NEBBIOLINA_PRIMARY');
      if (item.primary !== primary.value) {
        item.choices.querySelectorAll('input').forEach(choice => {
          if (choice.value === item.primary) choice.checked = false;
          if (choice.value === primary.value) choice.checked = true;
        });
        item.primary = primary.value;
      }
      item.primaryAvailable = primary.available;
      item.choices.querySelectorAll('input').forEach(choice => {
        const allowed = variants.some(variant => nebbiolinaPrimary(variants, variant.id)?.value === choice.value && variant.available === true);
        choice.disabled = choice.value === primary.value || !allowed;
      });
      this.syncProperties();
      this.syncSelectionFeedback();
    }
    syncProperties() {
      for (const item of this.fields) {
        if (item.skipped || item.field.behavior === 'rose-crown' || item.field.type !== 'color-swatches') continue;
        item.input.value = selectionValue(item.field, [...item.choices.querySelectorAll('input:checked')].map(el => el.value));
      }
    }
    hasCollision() {
      return this.fields.some(item => [...this.form.elements].some(control => control !== item.input && control.name === item.input.name && !control.disabled));
    }
    letteringChoiceContext() {
      const picker = this.letteringRoot?.querySelector('variant-selects[data-product-handle="scritta-su-nastro"]');
      const ui = picker?.querySelector('[data-cl-lettering-ui]');
      return ui ? { picker, ui } : null;
    }
    letteringChoiceReady() {
      const context = this.letteringChoiceContext();
      if (!context) return !this.letteringUiExpected;
      const { picker, ui } = context;
      const checked = [...ui.querySelectorAll('input[type="radio"]:checked')];
      const ids = [...this.form.elements].filter(control => control.name === 'id' && !control.matches(':disabled'));
      if (this.letteringUrlBlocked || picker.querySelectorAll('[data-cl-lettering-ui]').length !== 1
        || ui.dataset.clLetteringOptionName !== 'Colore della scritta' || checked.length !== 1 || ids.length !== 1) return false;
      const choice = checked[0];
      if (!choice.matches('[data-cl-lettering-choice]') || choice.disabled || choice.form !== this.form
        || choice.dataset.clLetteringCanonical !== choice.value || choice.dataset.optionName !== 'Colore della scritta'
        || !/^\d+$/.test(choice.dataset.optionValueId || '') || !/^\d+$/.test(choice.dataset.clLetteringVariantId || '')
        || choice.dataset.clLetteringVariantId !== String(ids[0].value)) return false;
      try {
        const variant = JSON.parse(picker.querySelector('[data-selected-variant]').textContent);
        return String(variant.id) === String(ids[0].value) && (variant.option1 ?? variant.options?.[0]) === choice.value;
      } catch {
        return false;
      }
    }
    prepareExtraLetteringSubmission() {
      const letteringContext = this.letteringChoiceContext();
      if (!letteringContext && this.letteringUiExpected) {
        this.message.textContent = this.letteringErrorLabel;
        this.message.hidden = false;
        return false;
      }
      if (letteringContext && !this.letteringChoiceReady()) {
        letteringContext.ui.setAttribute('aria-invalid', 'true');
        const note = letteringContext.ui.querySelector('[data-cl-lettering-legacy-note]');
        if (note) note.hidden = false;
        this.message.textContent = letteringContext.ui.dataset.clLetteringError;
        this.message.hidden = false;
        return false;
      }
      letteringContext?.ui.removeAttribute('aria-invalid');
      const marker = '[data-cl-extra-lettering-color]';
      const wrapper = this.form.querySelector(marker);
      if (!wrapper) {
        if (!letteringContext) return true;
        this.message.textContent = letteringContext.ui.dataset.clLetteringError;
        this.message.hidden = false;
        return false;
      }
      const inputs = [...wrapper.querySelectorAll('input')];
      const input = inputs[0];
      const fail = () => {
        if (!input) return this.fail();
        input.setAttribute('aria-invalid', 'true');
        if (!this.message.id) this.message.id = `${this.instance}-extra-error`;
        input.setAttribute('aria-errormessage', this.message.id);
        return this.fail({ field: { label: wrapper.querySelector('label')?.textContent.trim() || 'Colore Extra desiderato' }, wrapper, input });
      };
      if (this.form.querySelectorAll(marker).length !== 1 || inputs.length !== 1 || input.type !== 'text'
        || input.name !== 'properties[Colore Extra desiderato]' || input.form !== this.form) return fail();
      if (letteringContext) {
        const choice = letteringContext.ui.querySelector('input[type="radio"]:checked');
        if (wrapper.hidden !== (choice.value !== 'Extra')) return fail();
      }
      const successful = [...this.form.elements].filter(control => control.name === input.name && !control.matches(':disabled'));
      if (wrapper.hidden) {
        if (!input.matches(':disabled') || input.required || successful.length) return fail();
      } else {
        if (input.matches(':disabled') || !input.required || successful.length !== 1 || successful[0] !== input
          || typeof input.value !== 'string' || !input.value.trim() || input.value.length > 40) return fail();
      }
      wrapper.removeAttribute('aria-invalid');
      input.removeAttribute('aria-invalid');
      if (input.getAttribute('aria-errormessage') === this.message.id) input.removeAttribute('aria-errormessage');
      return true;
    }
    prepareSubmission() {
      if (!this.ready) return false;
      try { this.syncRoseFields(); this.syncNebbiolinaFields(); } catch { return this.fail(); }
      this.syncProperties();
      if (this.hasCollision()) return this.fail();
      for (const item of this.fields) {
        if (item.skipped) continue;
        if (item.input.matches(':disabled') || item.input.form !== this.form) return this.fail(item);
        if (item.field.behavior === 'rose-crown') {
          if (!item.input.value || item.input.value === 'Mix di colori') return this.fail(item);
          continue;
        }
        if (item.field.behavior === 'nebbiolina-variant' && (!item.primaryAvailable || ![...item.choices.querySelectorAll('input:checked')].every(choice => choice.value === item.primary || !choice.disabled))) return this.fail(item);
        const value = item.field.type === 'color-swatches' ? [...item.choices.querySelectorAll('input:checked')].map(el => el.value) : item.input.value;
        if (!validate(item.field, value)) return this.fail(item);
        item.wrapper.removeAttribute('aria-invalid');
      }
      if (!this.prepareExtraLetteringSubmission()) return false;
      this.message.hidden = true;
      return true;
    }
    fail(item) {
      this.message.textContent = item ? `${this.display(item.field.label)}: ${this.dataset.invalidLabel}` : this.dataset.clNativeMessageIncomplete;
      this.message.hidden = false;
      if (item) {
        item.wrapper.setAttribute('aria-invalid', 'true');
        (item.choices?.querySelector('input:not(:disabled)') || item.input).focus();
      }
      return false;
    }
  });
})();
